import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { Grid, FormLabel, Button, Table, TableBody, TableContainer, TableHead, TableRow, TableCell, ClickAwayListener, Typography } from '@material-ui/core';
import { SecondaryButton, SelectBox, TextBox } from '../../../../Core/FormInput';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import AddIcon from '@material-ui/icons/Add';
import { withStyles } from '@material-ui/styles';
import { getTCards_GetByRegNumberYardCheck, postTCard_YardCheck } from '../../../../Core/Service/checkInRole';
import { getTcardBranchData } from '../../../../Core/Service/TCardDashboard-service';
import moment from 'moment';
import AppContext from '../../../../App/AppContext';
import '../tCardScreenCommonStyle.scss';

const StyledTableCell = withStyles(() => ({
    head: {
        fontSize: 15,
        fontWeight: 600,
        backgroundColor: '#e0e0e0',
        padding: '6px 8px'
    },
    body: {
        fontSize: 14,
        padding: '6px 8px'
    }
}))(TableCell);

const StyledTableRow = withStyles(() => ({
    root: {
        cursor: 'pointer'
    }
}))(TableRow);

const YardChecksScreen = () => {
    const { showToast, portalSettings } = useContext(AppContext);

    const [regNumberToSearch, setRegNumberToSearch] = useState('');
    const [vehicleList, setVehicleList] = useState([]);
    const [selectedList, setSelectedList] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [branchList, setBranchList] = useState([]);
    const [state, setState] = useState({ branchID: null });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [btnLoader, setBtnLoader] = useState(false);
    const width = window.innerWidth;

    const legendItems = useMemo(() => {
        if (!portalSettings) return [];
        return ['CollectionDeliver', 'CustomerWaiting', 'Service', 'Inspection', 'MOT', 'Repair', 'NightShift', 'RecallJob', 'Tacho'].map((key) => ({
            label: portalSettings[`portalSettings${key}Text`],
            background: portalSettings[`portalSettings${key}BgColor`] || 'black',
            color: portalSettings[`portalSettings${key}TextColor`] || 'white',
            st: key.toLowerCase()
        }));
    }, [portalSettings]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await getTcardBranchData();
                if (res?.success && Array.isArray(res.data?.branches)) {
                    const branches = res.data.branches;
                    setBranchList(branches);
                    const dashboardData = JSON.parse(localStorage.getItem('_dashboard_') || '{}');
                    const matchedBranch = branches.find((b) => b.name === dashboardData.BranchPartReq);
                    setState((prev) => ({ ...prev, branchID: matchedBranch?.id || branches[0]?.id || null }));
                }
            } catch (error) {
                console.error('Error fetching TCard branch data:', error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchVehicles = async () => {
            try {
                const response = await getTCards_GetByRegNumberYardCheck(regNumberToSearch, state.branchID);
                setVehicleList(response.data?.list || []);
                setShowDropdown(true);
            } catch {
                setVehicleList([]);
                setShowDropdown(false);
            }
        };
        if (regNumberToSearch.length >= 2) fetchVehicles();
        else {
            setVehicleList([]);
            setShowDropdown(false);
        }
    }, [regNumberToSearch, state.branchID]);

    const handleVehicleToggle = (vehicle) => {
        const alreadySelected = selectedList.some((v) => v.tCardsRegistrationNumber === vehicle.tCardsRegistrationNumber && v.tCardsID === vehicle.tCardsID);
        if (alreadySelected) {
            showToast('Vehicle already selected');
            return;
        }
        setSelectedList([
            ...selectedList,
            {
                ...vehicle
            }
        ]);
        setVehicleList([]);
        setRegNumberToSearch('');
    };

    const handleAddTypedVehicle = () => {
        if (regNumberToSearch.trim().length >= 2) {
            const alreadyExists = selectedList.some((v) => v.tCardsRegistrationNumber === regNumberToSearch);
            if (alreadyExists) {
                showToast('Vehicle already added');
                return;
            }
            setSelectedList([
                ...selectedList,
                {
                    tCardsRegistrationNumber: regNumberToSearch,
                    status: 'T-card not found',
                    tCardsID: null
                }
            ]);
        } else {
            showToast('Enter at least 4 characters');
        }
    };

    const handleSubmit = async () => {
        const data = {
            branchID: state.branchID,
            vehicles: selectedList.map((v) => ({
                tCardID: v.tCardsID || null,
                regNumber: v.tCardsRegistrationNumber
            }))
        };
        setBtnLoader(true);
        const res = await postTCard_YardCheck(data);

        if (res.success && Array.isArray(res.data?.list)) {
            const updatedList = res.data.list.map((vehicle) => {
                const existing = selectedList.find((v) => v.tCardsRegistrationNumber === vehicle.regNumber);

                return {
                    ...existing,
                    ...vehicle,
                    status: vehicle.msg,
                    tCardsRegistrationNumber: vehicle.regNumber,
                    customer: vehicle.customer,
                    bd: vehicle.bookingDate
                };
            });
            setSelectedList(updatedList);
            setIsSubmitted(true);
            showToast('success', res.data.message || 'Yard check completed successfully!');
        }
        setBtnLoader(false);
    };

    const handleReset = () => {
        setSelectedList([]);
        setVehicleList([]);
        setRegNumberToSearch('');
        setIsSubmitted(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setState((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    return (
        <div style={{ padding: '16px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <Grid container spacing={2} alignItems="center" className="bg-white">
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <FormLabel style={{ paddingBottom: '8px' }}>Branch</FormLabel>
                    <SelectBox fullWidth name="branchID" value={state.branchID} onChange={handleChange} List={branchList} />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3} xl={2} style={{ position: 'relative' }}>
                    <FormLabel>Enter Registration Number</FormLabel>
                    <TextBox autoFocus name="regNumberToSearch" value={regNumberToSearch} onChange={(e) => setRegNumberToSearch(e.target.value)} />
                    {showDropdown && (
                        <ClickAwayListener onClickAway={() => setShowDropdown(false)}>
                            <div
                                style={{
                                    position: 'absolute',
                                    zIndex: 2,
                                    width: width > 650 ? '600px' : '100%',
                                    backgroundColor: 'white',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                                }}
                            >
                                {vehicleList.length > 0 ? (
                                    <TableContainer style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                        <Table>
                                            <TableBody>
                                                {vehicleList.map((vehicle) => (
                                                    <StyledTableRow
                                                        key={vehicle.loanVehicleID || vehicle.tCardsID}
                                                        onClick={() => handleVehicleToggle(vehicle)}
                                                        selected={selectedList.some((v) => v.tCardsID === vehicle.tCardsID)}
                                                    >
                                                        <TableCell colSpan={3}>
                                                            <Typography variant="body2" style={{ fontWeight: 600, color: vehicle.tcolor }}>
                                                                {vehicle.tCardsRegistrationNumber} Booked -{' '}
                                                                {vehicle.bd ? moment(vehicle.bd).format('DD/MM/YY') : 'N/A'} | {vehicle.customer || 'N/A'}
                                                            </Typography>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 4 }}>
                                                                {legendItems
                                                                    .filter((m) => vehicle[m.st])
                                                                    .map((m) => (
                                                                        <div key={m.st} style={{ margin: '2px' }}>
                                                                            <Typography
                                                                                style={{
                                                                                    fontSize: 10,
                                                                                    fontWeight: 600,
                                                                                    background: m.background,
                                                                                    color: m.color,
                                                                                    padding: '2px 6px',
                                                                                    borderRadius: 3
                                                                                }}
                                                                            >
                                                                                {m.label}
                                                                            </Typography>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                            {vehicle.notes && (
                                                                <Typography variant="caption" color="textSecondary">
                                                                    {vehicle.notes}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </StyledTableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <>Vehicle not found</>
                                )}
                            </div>
                        </ClickAwayListener>
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={1} xl={1}>
                    <Button fullWidth variant="contained" color="primary" style={{ marginTop: 22 }} onClick={handleAddTypedVehicle} startIcon={<AddIcon />}>
                        Add
                    </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={1} xl={1}>
                    {/* <Button
                        fullWidth
                        variant="contained"
                        color={isSubmitted ? 'default' : 'secondary'}
                        onClick={isSubmitted ? handleReset : handleSubmit}
                        disabled={selectedList.length === 0}
                        style={{ marginTop: 22 }}
                    >
                        {isSubmitted ? 'Reset' : 'Submit'}
                    </Button> */}
                    <SecondaryButton
                        fullWidth
                        isBtnLoader={btnLoader}
                        variant="contained"
                        color={isSubmitted ? 'default' : 'secondary'}
                        onClick={isSubmitted ? handleReset : handleSubmit}
                        disabled={selectedList.length === 0}
                        style={{ marginTop: 22 }}
                    >
                        {isSubmitted ? 'Reset' : 'Submit'}
                    </SecondaryButton>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer style={{ minHeight: '200px', maxHeight: '400px', overflowY: 'auto', border: '1px solid #ccc' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell></StyledTableCell>
                                    <StyledTableCell>Selected Vehicles</StyledTableCell>
                                    <StyledTableCell>Status</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No rows found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    selectedList.map((vehicle) => (
                                        <StyledTableRow key={vehicle.tCardsRegistrationNumber}>
                                            <TableCell>
                                                {!isSubmitted && (
                                                    <RemoveCircleIcon
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() =>
                                                            setSelectedList((prev) =>
                                                                prev.filter((v) => v.tCardsRegistrationNumber !== vehicle.tCardsRegistrationNumber)
                                                            )
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" style={{ fontWeight: 600, color: vehicle.tcolor }}>
                                                    Reg No. - {vehicle.tCardsRegistrationNumber || 'N/A'} | Booked -{' '}
                                                    {vehicle.bd ? moment(vehicle.bd).format('DD/MM/YY') : 'N/A'} | Customer -{' '}
                                                    {vehicle.customer || vehicle.customer || 'N/A'}
                                                </Typography>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 4 }}>
                                                    {legendItems
                                                        .filter((m) => vehicle[m.st])
                                                        .map((m) => (
                                                            <div key={m.st} style={{ margin: '2px' }}>
                                                                <Typography
                                                                    style={{
                                                                        fontSize: 10,
                                                                        fontWeight: 600,
                                                                        background: m.background,
                                                                        color: m.color,
                                                                        padding: '2px 6px',
                                                                        borderRadius: 3
                                                                    }}
                                                                >
                                                                    {m.label}
                                                                </Typography>
                                                            </div>
                                                        ))}
                                                </div>
                                                {vehicle.notes && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {vehicle.notes}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="primary">
                                                    {vehicle.status || 'Pending'}
                                                </Typography>
                                            </TableCell>
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </div>
    );
};

export default YardChecksScreen;
